import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
import { AgentCitation } from './agent.js';
/**
 * Slice 3b chat-history wedge — wire schemas + Firestore document shapes.
 *
 * Firestore paths:
 *   tenants/{tenantId}/users/{userId}/chats/{chatId}
 *   tenants/{tenantId}/users/{userId}/chats/{chatId}/messages/{messageId}
 *
 * See ~/.gstack/projects/kennisbank-suppohrt/WitteA-main-design-20260429-071106.md
 * for the full design doc + eng-review revisions.
 */
// ───────── Document field schemas ─────────────────────────────────────────
/**
 * Title lifecycle:
 *   pending     — chat just created, no title call has resolved yet
 *   generating  — Gemini Flash title call in flight (lease via titleGeneratedAt)
 *   generated   — title is final, no further auto-title work
 *
 * The design doc's Q4 self-heal: a chat with `titleStatus="pending"` OR
 * `titleStatus="generating"` AND lease > 30s old is fair game for a re-fire
 * on the next ask.
 */
export const ChatTitleStatus = z.enum(['pending', 'generating', 'generated', 'failed']);
export const ChatMessageRole = z.enum(['user', 'assistant']);
/**
 * Persisted chat-message source citation. Stored on assistant messages so the
 * case-file practitioner sees the exact Ring 1/Ring 2 sources that grounded
 * a 5-day-old answer. Reuses the `AgentCitation` wire shape verbatim — no
 * re-modelling, no schema drift across the backend↔agent↔storage boundary.
 */
export const ChatMessageSource = AgentCitation;
/**
 * Subcollection doc — one per turn (user or assistant).
 *
 * `tokensIn`/`tokensOut` mirror what `ai_usage_events` already records for
 * the assistant turn; storing them here lets the chat thread render token
 * usage in admin/debug views without joining BigQuery.
 */
/**
 * Per-turn user feedback on an assistant answer. Stored co-located with the
 * message so a single read of the chat thread hydrates the user's prior
 * verdict — no separate feedback endpoint. Overwriting a prior verdict
 * pushes the previous state onto `feedbackHistory` for a thin audit trail
 * on the document itself; the global `audit_log` collection still gets one
 * row per submission for security-grade traceability.
 */
export const ChatMessageFeedback = z.object({
    verdict: z.enum(['positive', 'negative']),
    comment: z.string().max(2000).optional(),
    submittedAt: IsoTimestamp,
    userId: DocId,
});
export const ChatMessageFeedbackHistoryEntry = z.object({
    verdict: z.enum(['positive', 'negative']),
    hasComment: z.boolean(),
    submittedAt: IsoTimestamp,
    userId: DocId,
});
/**
 * Persisted attachment record on a user message. The decoded text content is
 * NOT inlined on the Firestore doc (1 MB doc cap + cost) — it lives in GCS at
 * `gcsPath`. The metadata here is what the chat thread renders (filename
 * chip + size) and what the agent's multi-turn replay needs to re-inline
 * prior turns' files into the current ask payload.
 *
 * Authoritative bytes: GCS object at `gcsPath`. Firestore stores metadata
 * only — never the file contents.
 */
export const ChatMessageAttachment = z.object({
    filename: z.string().min(1).max(200),
    mimeType: z.string().min(1).max(100),
    /** Raw file size in bytes (matches the AgentAskRequest.attachment.sizeBytes input). */
    sizeBytes: z.number().int().nonnegative().max(5_000_000),
    /** Length of the decoded UTF-8 text content. Bounds the agent context budget. */
    textChars: z.number().int().nonnegative().max(1_000_000),
    /**
     * Fully-qualified GCS path: `gs://{bucket}/{tenantId}/{userId}/{chatId}/{messageId}/{filename}`.
     * Backend treats this as the canonical pointer; clients never construct or
     * mutate it.
     */
    gcsPath: z.string().min(1).max(500),
    /** sha256 hex of the decoded contentText — cheap dedup + integrity check. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
});
export const ChatMessage = z.object({
    messageId: DocId,
    role: ChatMessageRole,
    content: z.string().min(1).max(10000),
    sources: z.array(ChatMessageSource).default([]),
    modelId: z.string().min(1).max(100).optional(),
    tokensIn: z.number().int().nonnegative().optional(),
    tokensOut: z.number().int().nonnegative().optional(),
    /**
     * Attached file (XLSX/CSV/PDF-text/MD/etc.) the user uploaded with this
     * turn. Only set on user-role messages. Bytes live in GCS — see
     * {@link ChatMessageAttachment}. Multi-turn replay re-fetches prior
     * attachments and folds them back into the agent payload on follow-up
     * turns.
     */
    attachment: ChatMessageAttachment.optional(),
    /**
     * Per-turn answer feedback. Set on assistant messages when the user
     * clicks 👍/👎 (and optionally adds a comment). Overwriting is allowed —
     * the previous verdict is appended to `feedbackHistory`.
     */
    feedback: ChatMessageFeedback.optional(),
    feedbackHistory: z.array(ChatMessageFeedbackHistoryEntry).optional(),
    /**
     * Multi-turn debug surface. For assistant turns, the standalone Dutch
     * query the backend reformulator produced from the user's (often terse)
     * follow-up. Lets Andre see "your question was interpreted as: X" via
     * a future debug affordance. Absent on cold-start asks where no
     * reformulation happened.
     */
    reformulatedQuery: z.string().min(1).max(1000).optional(),
    /**
     * True when the assistant turn was persisted from a mid-stream client
     * disconnect (req.on('close')) rather than a clean stream completion.
     * Lets future UI render a "stream interrupted" affordance and lets the
     * orphan detector distinguish partial answers from true orphans.
     */
    truncated: z.boolean().optional(),
    /**
     * True when the assistant turn is an error placeholder written because the
     * agent call failed (cold-start timeout, upstream error, Jira fetch
     * failure, etc.). Lets the UI render a "couldn't answer — retry" turn and
     * keeps the thread paired (question + response) instead of leaving an
     * orphaned question. Distinct from `truncated` (a partial real answer).
     */
    failed: z.boolean().optional(),
    createdAt: IsoTimestamp,
});
/**
 * Parent chat doc. `lastQuestion` + `lastAnswerPreview` are denormalised so
 * the list endpoint never needs to fan out into the messages subcollection
 * (eng-review section 4 — N+1 elimination).
 *
 * `lastAnswerPreview` is capped at 80 chars per the eng-review #9 PII smear
 * mitigation (was 240; reduced to limit clinical-context exposure in the
 * sidebar list).
 */
export const ChatSummary = z.object({
    chatId: DocId,
    userId: DocId,
    tenantId: TenantId,
    title: z.string().min(1).max(200),
    titleStatus: ChatTitleStatus,
    /** Lease for the in-flight title call (cross-model T1 fix). */
    titleGeneratedAt: IsoTimestamp.optional(),
    messageCount: z.number().int().nonnegative(),
    sourceCount: z.number().int().nonnegative(),
    lastQuestion: z.string().max(2000),
    lastAnswerPreview: z.string().max(80),
    /** Tombstone — null on live chats, set on user-initiated delete (1C-C). */
    deletedAt: IsoTimestamp.nullable().default(null),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    /**
     * Client-supplied idempotency token (uuid v4) captured at the moment of
     * the "new chat" submit click. Persisted so the backend's in-memory
     * dedup map can be cross-referenced in debugging. Optional — older
     * chats predate the field and missing values are fine.
     */
    clientRequestId: z.string().min(1).max(120).optional(),
    schemaVersion: z.literal(1),
});
// ───────── Pagination cursor ──────────────────────────────────────────────
/**
 * Stable cursor across "Meer laden". Uses `(createdAt, chatId)` rather than
 * `(updatedAt, chatId)` — eng-review fix #4. updatedAt is mutated by every
 * write to the chat (new message, title arrival), so paginating on it
 * causes duplicates/skips while a parallel ask bumps the row.
 *
 * Encoded as a base64url JSON string on the wire so the frontend doesn't
 * have to reason about the shape.
 */
export const ChatCursor = z.object({
    createdAt: IsoTimestamp,
    chatId: DocId,
});
// ───────── Route request/response shapes ──────────────────────────────────
export const ListChatsRequest = z.object({
    /**
     * Page size. Default 50; capped at 100 to bound the response payload.
     */
    limit: z.coerce.number().int().min(1).max(100).default(50),
    /**
     * Opaque base64url cursor returned by a previous list response.
     * Absent on the first request.
     */
    cursor: z.string().min(1).max(500).optional(),
});
export const ListChatsResponse = z.object({
    chats: z.array(ChatSummary),
    nextCursor: z.string().min(1).max(500).nullable(),
});
export const GetChatResponse = z.object({
    chat: ChatSummary,
    messages: z.array(ChatMessage),
});
// ───────── Agent /v1/agent/title wire (eng-review 1A-B) ───────────────────
/**
 * POST /v1/agent/title — backend-to-agent call after the assistant message
 * has been written. Agent calls Gemini Flash with a 60-token Dutch prompt
 * and returns 4-6 word title. Returns `title: null` on English/refusal
 * fallback so the backend can leave `titleStatus="pending"` for self-heal.
 */
export const AgentTitleRequest = z.object({
    chatId: DocId,
    tenantId: TenantId,
    userId: DocId,
    firstQuestion: z.string().min(1).max(2000),
    firstAnswer: z.string().min(1).max(8000),
});
export const AgentTitleResponse = z.object({
    title: z.string().min(1).max(200).nullable(),
    modelId: z.string().min(1).max(100),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
});
//# sourceMappingURL=chat-history.js.map